


























































































import {
    computed,
    defineComponent,
    onBeforeMount,
    onBeforeUnmount,
    onMounted,
    PropType,
    ref,
} from '@vue/composition-api';
import { BankAccount } from '@core/models/user/Wallet';
import { errorGuard } from '@core/utils/ErrorGuard';
import { UserService } from '@core/services/user/UserService';
import {
    SdsButton,
    SdsButtonIcon,
    SdsDividerLine,
    SdsIcon,
    SdsSectionTitle,
} from '@superbet-group/web.lib.ui-components';
import { ModalStore } from '@store/modules/ui/shared/modal/modalIndex';
import {
    isMobileLayout,
    useI18n,
    useVuexStore,
} from '@superbet-group/web.lib.vue-utilities';
import { ModalCode } from '../modals/ModalCodes';
import { MfaFlow } from '@accountFederation/modules/securityCheck/faceId/mfaFlows';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import countryIndex from '@store/modules/data/country/countryIndex';
import { captiveKycStore } from '@accountFederation/store/data/captiveKyc/captiveKycStore';
import { brazilBanksService } from '@core/services/brazilBanks/BrazilBanksService';
import { useUserVerificationChecks } from '@walletFederation/src/composables/useUserVerificationChecks';

export type BankAccountManagementParentPage = 'deposit' | 'withdrawal' | 'accountDetails';

export default defineComponent({
    name: 'BankAccountsManagement',
    props: {
        pageType: {
            type: String as PropType<BankAccountManagementParentPage>,
            required: true,
        },
    },
    emits: ['bank-accounts'],
    setup(props, { emit }) {
        const { translate } = useI18n();

        const isFaceIdRequired = ref<boolean>(false);
        const bankAccounts = ref<BankAccount[] | null>(null);
        const bankAccountsLength = computed(() => bankAccounts?.value?.length ?? 0);
        const bankAccountsMax = 3;
        const isLoading = ref<boolean>(false);
        const bankOptions = ref<{ name: string, value: string }[]>([]);

        const modalModule = useVuexStore<ModalStore>('ui/modal');
        const setModal = modalModule.action('setModal');

        const countryModule = useVuexStore<typeof countryIndex>('data/country');
        const countryConfig = countryModule.getter('config');
        const bankAccountsConfig = countryConfig.value.wallet.numberOfBankAccountsToShowBankManagement;

        const captiveKycModule = useVuexStore<typeof captiveKycStore>('data/captiveKyc');
        const hasIncompleteVerification = captiveKycModule.getter('hasIncompleteVerification');
        const { onFaceIdCheck } = useUserVerificationChecks({ triggerVerificationFor: MfaFlow.BankAccountManagement });

        const isWithdrawal = props.pageType === 'withdrawal';
        const isDeposit = props.pageType === 'deposit';

        const onAddBankAccount = () => {
            setModal({
                code: ModalCode.addBankAccount,
                data: {
                    verifyUser: onFaceIdCheck,
                    isFaceIdRequired: isFaceIdRequired.value,
                    onSuccess: () => {
                        getRegisteredBanks();
                        setModal({ code: ModalCode.addBankSuccess });
                    },
                },
            });
        };

        const getRegisteredBanks = () =>
            errorGuard(async () => {
                bankAccounts.value = await UserService.getInstance().getRegisteredBanks();
                emit('bank-accounts', bankAccounts.value);
            });

        const getBankName = (ispb: string) => {
            return bankOptions.value.find((x) => x.value === ispb)?.name;
        };

        onBeforeMount(async () => {
            isLoading.value = true;
            await getRegisteredBanks();
            isLoading.value = false;
        });

        let featureFaceIdRequiredSubscriptionUnsubscribeFn: () => void;

        onBeforeMount(async () => {
            const brazilBankAccounts = await brazilBanksService.getBrazilBanks();
            bankOptions.value = brazilBankAccounts.map((x) => ({ name: x.tradingName, value: x.ispb })) || [];
        });

        onMounted(() => {
            featureFaceIdRequiredSubscriptionUnsubscribeFn = featureFlagService.subscribeToFeatureChanges('wallet.bankAccountsManagement.isFaceIdRequired', (value) => {
                isFaceIdRequired.value = value;
            }, false);
        });

        onBeforeUnmount(() => {
            featureFaceIdRequiredSubscriptionUnsubscribeFn?.();
        });

        const shouldShowDeleteButton = computed(() => {
            return registeredBankAccounts.value && registeredBankAccounts.value.length > 1;
        });

        const deleteBankAccount = (bankAccount: BankAccount, otp?: string) => {
            const { id: deviceId } = bankAccount;
            const details = {
                registered: false,
                device_id: deviceId,
                otp: otp,
            };
            errorGuard(async () => {
                await UserService.getInstance().unregisterBankAccount(details);
                getRegisteredBanks();
            });
        };

        const onDeleteBankAccount = (bankAccount: BankAccount) => {
            setModal({
                code: ModalCode.removeBankAccount,
                data: {
                    onRemove: () => {
                        if (isFaceIdRequired.value) {
                            onFaceIdCheck((mfaPayload) => deleteBankAccount(bankAccount, mfaPayload?.otpId));
                        } else {
                            deleteBankAccount(bankAccount);
                        }
                    },
                },
            });
        };

        const registeredBankAccounts = computed(() => bankAccounts.value?.filter((bankAccount) => bankAccount.registered));

        const shouldShowAddAccountButton = computed(() => registeredBankAccounts.value && registeredBankAccounts.value.length < 3);

        const bankAccountsProgress = computed(() => bankAccountsLength.value / bankAccountsMax * 100);

        const tooltipText = computed(() => translate(
            'wallet.bankAccountsManagement.infoTooltip',
            {
                bankAccountsLength: bankAccountsLength.value,
                bankAccountsMax: bankAccountsMax,
            },
        ));

        // User will be redirected to kyc process on withdrawal and deposit page if incomplete verification
        // Hide the component just in case

        const isBankAccountsManagementVisible = computed(() => {
            const isVerificationComplete = !hasIncompleteVerification.value;
            if (isDeposit) {
                return isVerificationComplete && bankAccountsLength.value >= bankAccountsConfig;
            }
            return isVerificationComplete;
        },
        );

        const isMobile = computed(() => isMobileLayout());

        return {
            registeredBankAccounts,
            isLoading,
            onDeleteBankAccount,
            shouldShowAddAccountButton,
            shouldShowDeleteButton,
            onAddBankAccount,
            bankAccountsLength,
            bankAccountsMax,
            bankAccountsProgress,
            tooltipText,
            isBankAccountsManagementVisible,
            isMobile,
            bankOptions,
            getBankName,
            getRegisteredBanks,
            isWithdrawal,
        };
    },
    components: {
        SdsButtonIcon,
        SdsButton,
        SdsDividerLine,
        SdsIcon,
        SdsSectionTitle,
    },
});

